import React, { useCallback, useEffect, useState } from 'react';
import { Button, Collapse, Form, Input, Modal, Popconfirm, Radio, Select, Space, Table, Tag, Tooltip, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import tmService from '../../../services/tmService';
import { fmtTime, ok, toastErr } from './common';

const REASON = { hard_bounce: '硬退信', complaint: '投诉', unsubscribe: '退订', manual: '手动' };
const SCOPE = { global: { color: 'red', text: '全局' }, user: { color: 'blue', text: '账号' }, stream: { color: 'default', text: '本流' } };

const Suppressions = ({ stream }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({});
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmService.listSuppressions({
                stream_id: stream.id, page: page.current, per_page: page.pageSize,
                email: f.email || undefined, reason: f.reason, scope: f.scope,
            });
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [stream.id, page, f, t]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };

    const add = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await tmService.createSuppression({
                emails: v.emails, scope: v.scope, stream_id: stream.id, category: v.category || undefined,
            });
            if (ok(r)) {
                const inv = r.data?.invalid || [];
                (inv.length ? message.warning : message.success)(
                    inv.length ? t('Added {{n}}, {{m}} invalid: {{e}}', { n: r.data.added, m: inv.length, e: inv.slice(0, 5).join(', ') })
                        : t('Added {{n}}', { n: r.data?.added || 0 }),
                );
                setAdding(false);
                form.resetFields();
                load();
            }
        } catch (e) {
            toastErr(e, t('Failed to add'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (r) => {
        try {
            const res = await tmService.deleteSuppression(r.id);
            if (ok(res)) { message.success(t('Removed')); load(); }
        } catch (e) {
            toastErr(e, t('Failed to remove'));
        }
    };

    const columns = [
        { title: t('Email'), dataIndex: 'email', ellipsis: true },
        { title: t('Scope'), dataIndex: 'scope', width: 80, render: (v) => <Tag color={SCOPE[v]?.color}>{t(SCOPE[v]?.text || v)}</Tag> },
        { title: t('Reason'), dataIndex: 'reason', width: 90, render: (v) => t(REASON[v] || v) },
        { title: t('Category'), dataIndex: 'category', width: 110, render: (v) => v || t('All'), responsive: ['md'] },
        { title: t('Time'), dataIndex: 'created_at', width: 160, render: fmtTime, responsive: ['md'] },
        {
            title: '', key: 'op', width: 80,
            render: (_, r) => (r.scope === 'global' ? (
                <Tooltip title={t('Hard bounces and complaints are blocked globally; contact support to remove')}><Tag style={{ marginInlineEnd: 0 }}>{t('Read-only')}</Tag></Tooltip>
            ) : (
                <Popconfirm title={t('Once removed, you can send to this address again')} onConfirm={() => remove(r)}>
                    <Button type="link" size="small" danger>{t('Remove')}</Button>
                </Popconfirm>
            )),
        },
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>{t('Add')}</Button>
                <Tooltip title={t('Enter a full address to also see global blocks')}>
                    <Input.Search allowClear placeholder={t('Email')} style={{ width: 220 }} onSearch={(v) => setFilter({ email: v.trim() })} />
                </Tooltip>
                <Select allowClear placeholder={t('Reason')} style={{ width: 110 }} value={f.reason} onChange={(v) => setFilter({ reason: v })}
                    options={Object.keys(REASON).map((k) => ({ value: k, label: t(REASON[k]) }))} />
                <Select allowClear placeholder={t('Scope')} style={{ width: 100 }} value={f.scope} onChange={(v) => setFilter({ scope: v })}
                    options={Object.keys(SCOPE).map((k) => ({ value: k, label: t(SCOPE[k].text) }))} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns}
                pagination={{ ...page, total, showSizeChanger: true, onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <Modal open={adding} title={t('Add to suppression list')} onOk={add} confirmLoading={saving} onCancel={() => setAdding(false)} destroyOnClose>
                <Form form={form} layout="vertical" initialValues={{ scope: 'stream' }}>
                    <Form.Item name="emails" label={t('Email')} rules={[{ required: true, message: t('Enter an email address') }]}>
                        <Input.TextArea rows={4} placeholder={t('One per line, up to 1,000')} />
                    </Form.Item>
                    <Form.Item name="scope" label={t('Scope')}>
                        <Radio.Group>
                            <Radio value="stream">{t('This stream')}</Radio>
                            <Radio value="user">{t('Whole account')}</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Collapse ghost size="small" items={[{
                        key: 'adv', label: t('Advanced'),
                        children: (
                            <Form.Item name="category" label={t('Block this category only')} style={{ marginBottom: 0 }}
                                rules={[{ pattern: /^[A-Za-z0-9_.-]{1,64}$/, message: t('Invalid category code') }]}>
                                <Input placeholder="newsletter" />
                            </Form.Item>
                        ),
                    }]} />
                </Form>
            </Modal>
        </div>
    );
};

export default Suppressions;
